/**
 * Results Hook - Real-time results from Firestore
 * REPLACES: WebApp_Server.js _countResults_() and webGetState()
 */

import { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  limit
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useEffectiveClientId } from './useImpersonation';

export interface Result {
  id: string;
  date: Timestamp;
  firstName: string;
  lastName: string;
  phone: string;
  summary: string;
  quality: string;
  interested: string;
  recordingUrl: string;
  callDuration: number;
  classification: string;
  run: number;
  correctName: string;
  emailGiven: string;
}

interface ResultCounts {
  good: number;
  later: number;
  bad: number;
  notInterested: number;
  nonAccredited: number;
  pending: number;
  total: number;
}

export function useResults(classification?: string, maxResults = 100) {
  const effectiveClientId = useEffectiveClientId();
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<ResultCounts>({
    good: 0,
    later: 0,
    bad: 0,
    notInterested: 0,
    nonAccredited: 0,
    pending: 0,
    total: 0
  });

  useEffect(() => {
    if (!effectiveClientId) {
      setLoading(false);
      return;
    }

    const resultsRef = collection(db, 'clients', effectiveClientId, 'results');

    // Build query
    let q;
    if (classification === 'good_all') {
      // Combined: good_leads + good_leads_later
      q = query(
        resultsRef,
        where('classification', 'in', ['good_leads', 'good_leads_later']),
        orderBy('date', 'desc'),
        limit(maxResults)
      );
    } else if (classification) {
      q = query(
        resultsRef,
        where('classification', '==', classification),
        orderBy('date', 'desc'),
        limit(maxResults)
      );
    } else {
      q = query(
        resultsRef,
        orderBy('date', 'desc'),
        limit(maxResults)
      );
    }

    // Real-time listener - updates instantly when webhook writes
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Result));

      setResults(data);
      setLoading(false);

      // Count by classification
      if (!classification) {
        const newCounts: ResultCounts = {
          good: 0,
          later: 0,
          bad: 0,
          notInterested: 0,
          nonAccredited: 0,
          pending: 0,
          total: data.length
        };

        data.forEach(r => {
          switch (r.classification) {
            case 'good_leads':
              newCounts.good++;
              break;
            case 'good_leads_later':
              newCounts.later++;
              break;
            case 'bad_leads':
              newCounts.bad++;
              break;
            case 'not_interested':
              newCounts.notInterested++;
              break;
            case 'non_accredited':
              newCounts.nonAccredited++;
              break;
            default:
              newCounts.pending++;
          }
        });

        setCounts(newCounts);
      }
    }, (error) => {
      console.error('Results listener error:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [effectiveClientId, classification, maxResults]);

  return { results, loading, counts };
}
